import { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/kfmApi";
import { ImagePlus, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Optional custom Open Graph image for a shared study/sermon. If the owner uploads one it
// becomes the link-preview image; otherwise the auto-generated branded card is used.
export default function ShareImagePicker({ token, imageUrl, initialCustom = false, testid = "share-img", onChange }) {
  const [custom, setCustom] = useState(initialCustom);
  const [busy, setBusy] = useState(false);
  const [bust, setBust] = useState(Date.now());
  const fileRef = useRef(null);

  if (!token) return null;
  const base = imageUrl || `${BACKEND}/api/share/${token}/image.png`;
  const preview = `${base}${base.includes("?") ? "&" : "?"}cb=${bust}`;

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image (JPG or PNG)."); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("That image is too large (max 8MB)."); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file, file.name || "share.jpg");
    try {
      await api.post(`/share-image/${token}`, fd);
      setCustom(true);
      const t = Date.now();
      setBust(t);
      onChange?.(t, true);
      toast.success("Preview image updated — this is what people will see when you share the link.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't upload that image");
    }
    setBusy(false);
  };

  const reset = async () => {
    setBusy(true);
    try {
      await api.delete(`/share-image/${token}`);
      setCustom(false);
      const t = Date.now();
      setBust(t);
      onChange?.(t, false);
      toast.success("Back to the branded card");
    } catch (err) {
      toast.error("Couldn't reset the image");
    }
    setBusy(false);
  };

  return (
    <div data-testid={`${testid}-block`} className="rounded-xl border border-[#29344D] p-3 space-y-2.5">
      <div className="text-xs font-medium uppercase tracking-widest text-slate-500">Link preview image</div>
      <div className="flex items-center gap-3">
        <div className="w-28 h-[59px] shrink-0 rounded-lg overflow-hidden border border-[#29344D] bg-[#0B0F16]">
          <img data-testid={`${testid}-preview`} src={preview} alt="Link preview"
               className="w-full h-full object-cover" />
        </div>
        <div className="min-w-0 text-xs text-slate-400 leading-relaxed">
          {custom ? "Your own image is used when this link is shared." : "A branded card is used by default. Upload a photo to use your own."}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <input ref={fileRef} type="file" accept="image/*" onChange={onPick} className="hidden" data-testid={`${testid}-input`} />
        <button data-testid={`${testid}-upload`} onClick={() => fileRef.current?.click()} disabled={busy}
                className="h-9 px-3 rounded-full bg-[#10222A] text-[#00E5FF] hover:bg-[#10222A] text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50 transition-colors">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" strokeWidth={1.6} />}
          {custom ? "Replace image" : "Upload an image"}
        </button>
        {custom && (
          <button data-testid={`${testid}-reset`} onClick={reset} disabled={busy}
                  className="h-9 px-3 rounded-full border border-[#29344D] text-slate-400 hover:text-slate-300 text-xs font-medium flex items-center gap-1.5 disabled:opacity-50 transition-colors">
            <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.6} /> Use branded card
          </button>
        )}
      </div>
    </div>
  );
}
